<template>
  <v-card dark class="ModalBecomeDeveloper">
    <div class="d-flex justify-space-between align-center mb-4">
      <v-btn icon color="white darken-3" class="btn-status">
        <v-icon>mdi-code-tags</v-icon>
      </v-btn>
      <v-btn icon text color="n3" @click="close">
        <v-icon>close</v-icon>
      </v-btn>
    </div>
    <h2 class="ModalBecomeDeveloper__title">Become a Forge Plugin Developer</h2>
    <div class="ModalBecomeDeveloper__content">
      <p class="ModalBecomeDeveloper__description">
        Join the Forge Plugin Developer community and start building powerful extensions for Forge sites.
      </p>
      <div class="ModalBecomeDeveloper__benefits">
        <h3 class="ModalBecomeDeveloper__benefits-title">What you can do as a Plugin Developer:</h3>
        <ul class="ModalBecomeDeveloper__benefits-list">
          <li>Build and publish custom plugins to extend Forge functionality</li>
          <li>Reach thousands of Forge users through the Plugin Marketplace</li>
          <li>Monetize your plugins and grow your developer business</li>
          <li>Access comprehensive SDK tools and APIs</li>
        </ul>
      </div>
      <div class="ModalBecomeDeveloper__docs-link">
        <a 
          href="https://docs.getforge.com/guides/about-forge-plugins/" 
          target="_blank" 
          rel="noopener noreferrer"
          class="ModalBecomeDeveloper__link"
        >
          <v-icon small class="mr-1">mdi-open-in-new</v-icon>
          Learn more about Forge Plugins
        </a>
      </div>
    </div>
    <div v-if="loading" class="ModalBecomeDeveloper__loading">
      <v-progress-circular indeterminate color="y1" size="24"></v-progress-circular>
      <span class="ml-2">Creating developer account...</span>
    </div>
    <div v-if="error" class="ModalBecomeDeveloper__error">
      <v-icon color="r1" class="mr-1">mdi-alert-circle</v-icon>
      <span>{{ error }}</span>
    </div>
    <div class="ModalBecomeDeveloper__footer">
      <v-btn color="n5" @click="close" :disabled="loading"> Cancel </v-btn>
      <v-btn @click="createDeveloper" color="y1" :disabled="loading" :loading="loading">
        Become a Developer
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import { marketplaceAPI } from '@/services/marketplace'
import { mapState } from 'vuex'

export default {
  name: 'ModalBecomeDeveloper',
  data() {
    return {
      loading: false,
      error: null,
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.user.current,
    }),
  },
  methods: {
    async createDeveloper() {
      this.loading = true
      this.error = null

      try {
        // Check if developer already exists
        const existingDeveloper = await marketplaceAPI.findDeveloperByEmail(this.user.email)
        
        if (existingDeveloper) {
          this.error = 'You are already registered as a developer!'
          this.loading = false
          return
        }

        // Create developer using user information
        // Note: country should be ISO 3166-1 alpha-2 code (e.g., "US", "GB", "CA") per API spec
        const developerData = {
          name: this.user.name || this.user.email.split('@')[0],
          email: this.user.email,
          user_id: this.user.id?.toString() || null,
          country: this.user.country || null,
          verified: false,
          status: 'active',
        }

        const result = await marketplaceAPI.createDeveloper(developerData)
        
        // Close this modal first
        this.close()
        
        // Show success message
        this.$store.commit('application/openModal', {
          component: 'ModalInformation',
          props: {
            title: 'Welcome to the Developer Community!',
            text: 'Your developer account has been created successfully. You can now start building and publishing plugins.',
            actionBtnLabel: 'Get Started',
            status: 'success',
            action: () => {
              this.$store.commit('application/closeModal')
              // Emit event after success modal closes to refresh developer status
              this.$root.$emit('developer-created')
            },
          },
        })
        
        // Also emit immediately in case the user doesn't interact with the success modal
        // The handler will add a delay to allow API processing
        this.$root.$emit('developer-created')
      } catch (error) {
        console.error('Error creating developer:', error)
        this.error = error.message || 'Failed to create developer account. Please try again later.'
      } finally {
        this.loading = false
      }
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
$style: ModalBecomeDeveloper;
.#{$style} {
  &.theme--dark.v-card {
    @extend %modal-body-styles;
    min-width: 500px;
    max-width: 600px;
    padding: 24px 24px 0;
  }
  &__title {
    font-weight: 500;
    font-size: $H20;
    color: $white;
    margin-bottom: 20px;
  }
  &__content {
    margin-bottom: 24px;
  }
  &__description {
    @extend %label-styles;
    margin-bottom: 20px;
    line-height: 1.6;
  }
  &__benefits {
    margin-bottom: 20px;
  }
  &__benefits-title {
    font-size: $H14;
    font-weight: 500;
    color: $white;
    margin-bottom: 12px;
  }
  &__benefits-list {
    @extend %label-styles;
    padding-left: 20px;
    margin: 0;
    li {
      margin-bottom: 8px;
      line-height: 1.5;
    }
  }
  &__docs-link {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid $N5;
  }
  &__link {
    display: inline-flex;
    align-items: center;
    color: $Y1;
    text-decoration: none;
    font-size: $H14;
    transition: opacity 0.2s;
    &:hover {
      opacity: 0.8;
      text-decoration: underline;
    }
  }
  &__loading {
    display: flex;
    align-items: center;
    padding: 16px 0;
    color: $N2;
    font-size: $H14;
  }
  &__error {
    display: flex;
    align-items: center;
    padding: 12px;
    margin-bottom: 16px;
    background: rgba(255, 71, 71, 0.1);
    border: 1px solid rgba(255, 71, 71, 0.3);
    border-radius: 4px;
    color: #ff4747;
    font-size: $H14;
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px 0;
    gap: 16px;
    & > .theme--dark.v-btn {
      color: $white;
    }
  }
}
</style>

